import { Button, IconButton, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { deleteHandle } from './actions';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import Box from '@mui/material/Box';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { mainPageRootStyle } from '../styles';
import { Link } from 'react-router-dom';
import EmptyState from '../components/EmptyState';
import Breadcrumb from '../components/Breadcrumb';
import ConfirmDialog from '../components/ConfirmDialog';

const HandlesListPage = () => {
    const dispatch = useDispatch();
    const handles = useSelector(state => state.handle.handles);
    const userId = useSelector(state => state.auth.user?.uid);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [handleToDelete, setHandleToDelete] = useState(null);
    const navigate = useNavigate();

    React.useEffect(() => {
        document.title = 'Handles - Sky Toolbox';
    }, []);

    const handleDelete = (handle) => {
        setHandleToDelete(handle);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = () => {
        dispatch(deleteHandle(handleToDelete.id, navigate, userId));
        setDeleteDialogOpen(false);
    };

    const cancelDelete = () => {
        setHandleToDelete(null);
        setDeleteDialogOpen(false);
    };

    return (
        <Paper style={mainPageRootStyle} sx={{ p: 3 }}>
            <Breadcrumb items={[
                { label: 'Handles' }
            ]} />
            
            {/* Page Header */}
            <Box 
                sx={{ 
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    borderRadius: 3,
                    p: 3,
                    mb: 4,
                    color: 'white',
                }}
            >
                <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 1 }}>
                    👤 Handles
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Manage your BlueSky accounts
                </Typography>
            </Box>
            
            <Box display="flex" justifyContent="center" flexWrap="wrap" gap={2} sx={{ mb: 4 }}>
                <Button 
                    variant="contained" 
                    component={Link} 
                    to="/handles/add"
                    size="large"
                    sx={{ 
                        minWidth: 200,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #5568d3 0%, #63398b 100%)',
                        }
                    }}
                >
                    Add Handle
                </Button>
            </Box>
            {(!handles || handles.length === 0) ? (
                <EmptyState
                    title="No handles configured"
                    message="Add your first handle to get started"
                />
            ) : (
                <TableContainer component={Paper} elevation={2}>
                    <Table sx={{ minWidth: 650 }} aria-label="handles table">
                        
                        <TableBody>
                            {(handles ?? []).map((handle) => (
                                <TableRow
                                    key={handle.id}
                                    sx={{ 
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        '&:hover': { 
                                            bgcolor: 'action.hover',
                                        }
                                    }}
                                >
                                    <TableCell>
                                        <Typography variant="body1" fontWeight="medium">
                                            @{handle.handle}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton 
                                            component={Link} 
                                            to={`/handles/edit/${handle.id}`}
                                            sx={{ 
                                                color: 'primary.main',
                                                '&:hover': { bgcolor: 'primary.light', color: 'primary.dark' }
                                            }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton 
                                            onClick={() => handleDelete(handle)}
                                            sx={{ 
                                                color: 'error.main',
                                                '&:hover': { bgcolor: 'error.light', color: 'error.dark' }
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <ConfirmDialog
                open={deleteDialogOpen}
                onClose={cancelDelete}
                onConfirm={confirmDelete}
                title="Delete Handle"
                message={`Are you sure you want to delete handle '${handleToDelete?.handle}'?`}
                confirmText="Delete"
                cancelText="Cancel"
                confirmColor="error"
                autoFocusConfirm={true}
            />
        </Paper>
    );
};

export default HandlesListPage;
